import { Button, Input } from 'components/atoms';
import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { useHasPermission } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGenerateEan } from 'services/codesDistributor/ean';
import {
  useGetGenres,
  useGetLanguages,
  useGetPublisherLightList,
} from 'services/enums';
import { environment } from 'services/environment/Environment';
import {
  DIGITAL_PRODUCT_TYPE,
  RELEASE_TYPE,
} from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { ARTIST_ROLE, STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { getImageSize } from 'utils/components/inputs/image';
import {
  getYearsSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import { getLanguageSelectOptions } from 'utils/global/language.utils';
import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { array, boolean, date, mixed, number, object, string } from 'yup';
import { IFormProps, IFormikProps } from '../types';
import MusicProductDigitalArtists from './tabs/MusicProductDigitalArtists';
import MusicProductDigitalAssetIds from './tabs/MusicProductDigitalAssetIds';
import MusicProductDigitalContract from './tabs/MusicProductDigitalContract';
import MusicProductDigitalContractFuture from './tabs/MusicProductDigitalContractFuture';
import MusicProductDigitalContractHistory from './tabs/MusicProductDigitalContractHistory';
import MusicProductDigitalDelivery from './tabs/MusicProductDigitalDelivery';
import MusicProductDigitalDeliveryAll from './tabs/MusicProductDigitalDeliveryAll';
import MusicProductDigitalDeliveryDetail, {
  digitalDeliveryValidator,
} from './tabs/MusicProductDigitalDeliveryDetail';
import MusicProductDigitalExternal from './tabs/MusicProductDigitalExternal';
import MusicProductDigitalSmartLink from './tabs/MusicProductDigitalSmartLink';
import MusicProductDigitalStatistics from './tabs/MusicProductDigitalStatistics';
import MusicProductDigitalTitles from './tabs/MusicProductDigitalTitles';
import MusicProductDigitalTracks, {
  albumValidation,
} from './tabs/MusicProductDigitalTracks';
import MusicProductDigitalVideo from './tabs/MusicProductDigitalVideo';

export enum musicProductDigitalTabs {
  contracts,
  tracks,
  video,
  artists,
  titles,
  external,
  delivery,
  statistics,
  smartlink,
  assetIds,
}

export const marketingInitialValues = {
  smartlink: '',
  linkName: '',
  domainName: '',
};

export const musicProductDigitalCopyrights = {
  name: '',
  year: '',
};

export const musicProductDigitalInitialValues: IMusicProductDigitalForm = {
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  releaseStatus: STATUS.draft,
  releaseType: RELEASE_TYPE.unknown,
  releaseDate: '',
  genre: { id: -1, name: '' },
  ean: '',
  pLine: musicProductDigitalCopyrights,
  cLine: { name: '', year: '' },
  productImageLink: '',
  productImagePreviewLink: '',
  artists: [],
  externalIds: [],
  otherTitles: [],
  tracks: [],
  settlementInfo: {
    contracts: [],
    perTrackSettlement: false,
    perTrackContracts: [],
  },
  smartlink: '',
  marketing: marketingInitialValues,
  productType: DIGITAL_PRODUCT_TYPE.audio,
  videoId: '',
  video: null,
  hasDeliveryDiscrepancy: null,
  thumbnailImageLink: '',
  deliveryHasErrors: null,
};

export const artistsValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  role: requiredEnumSelect(ARTIST_ROLE),
});
export const settlementInfoContractValidation = object().shape({
  contractId: string()
    .trim()
    .required(() => i18next.t('formik-error.required')),
  labelId: string().required(() => i18next.t('formik-error.required')),
  startDate: string().required(() => i18next.t('formik-error.required')),
  divisionPercentage: number().required(() =>
    i18next.t('formik-error.required')
  ),
});
export const settlementInfoPerTrackContractValidation = object().shape({
  contracts: array().of(settlementInfoContractValidation),
});
export const settlementInfoValidation = object().shape({
  perTrackSettlement: boolean().when('contracts', {
    is: (contracts: any[]) => !!contracts.length,
    then: schema => schema.required(() => i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable(),
  }),
  contracts: array().of(settlementInfoContractValidation),
  perTrackContracts: array().of(settlementInfoPerTrackContractValidation),
});

// const pLineValidator = yup.object().shape({
//   name: ,

//   year: ,
// });

// const cLineValidator = yup.object().shape({
//   name: yup.lazy((value, context) => {
//     const year = context.parent.year;
//     value = value ?? '';
//     if (year && value.trim().length === 0) {
//       return yup.string().required(() => i18next.t('formik-error.required'));
//     }
//     return yup.string().nullable();
//   }),

//   year: yup.lazy((value, context) => {
//     const name = context.parent.name;
//     value = value ?? '';

//     if (name && name.trim().length > 0) {
//       return yup
//         .string()
//         .not([''], () => i18next.t('formik-error.option-unknown-value'))
//         .required(() => i18next.t('formik-error.required'));
//     }
//     return yup.string().nullable();
//   }),
// });

const musicProductDigitalValidationSchema = object().shape({
  releaseStatus: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  pLine: credensialsValidation('releaseStatus'),
  cLine: credensialsValidation('releaseStatus'),
  ean: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'releaseStatus'
  ),
  releaseType: conditionalField(
    requiredEnumSelect(RELEASE_TYPE),
    'releaseStatus'
  ),
  releaseDate: conditionalField(
    date().required(() => i18next.t('formik-error.required')),
    'releaseStatus'
  ),
  genre: conditionalField(
    object().shape({
      id: number().required(() => i18next.t('formik-error.required')),
      name: string().required(() => i18next.t('formik-error.required')),
    }),
    'releaseStatus'
  ),
  artists: array()
    .of(artistsValidation)
    .when('releaseStatus', {
      is: (status?: STATUS) =>
        status !== STATUS.draft && status !== STATUS.unknown,
      then: schema =>
        schema
          .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
          .test(
            'has-mpd-main-artist',
            () =>
              i18next.t('formik-error.what-should-have-main-artist', {
                what: i18next.t('track.what'),
              }),
            artist =>
              artist &&
              artist.some(item => item.role === ARTIST_ROLE.mainArtist)
          ),
      otherwise: schema => schema,
    }),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  tracks: array().when('productType', {
    is: (productType: DIGITAL_PRODUCT_TYPE) =>
      productType === DIGITAL_PRODUCT_TYPE.audio,
    then: schema =>
      schema.of(albumValidation).when('releaseStatus', {
        is: (status?: STATUS) =>
          status !== STATUS.draft && status !== STATUS.unknown,
        then: schema =>
          schema
            .min(1, () => i18next.t('formik-error.at-least-one-album-required'))
            .required(() => i18next.t('formik-error.required')),
        otherwise: schema => schema,
      }),
    otherwise: schema => schema.nullable().optional(),
  }),
  productImageLink: string().nullable(),
  file: conditionalField(
    mixed().when('productImageLink', {
      is: (value?: string) => !!value?.length,
      then: schema => schema.notRequired(),
      otherwise: schema =>
        schema.required(() => i18next.t('formik-error.required')),
    }),
    'releaseStatus'
  ),
  settlementInfo: settlementInfoValidation,
  delivery: digitalDeliveryValidator,
});

export interface IMusicProductDigitalFormProps
  extends Omit<FormikConfig<IMusicProductDigitalForm>, 'initialValues'>,
    IFormProps,
    II18nextPrefix {
  initialValues?: IMusicProductDigitalForm;
}

const MusicProductDigitalForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = musicProductDigitalInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  validationSchema = musicProductDigitalValidationSchema,
  ...props
}: IMusicProductDigitalFormProps) => {
  const { t } = useTranslation();
  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();
  const hasPermission = useHasPermission();

  const publishers = useGetPublisherLightList();

  const generateEan = useGenerateEan();

  const languages = useGetLanguages();

  const genres = useGetGenres();

  const { pathname } = useLocation();

  const navigate = useNavigate();

  return (
    <Formik
      {...props}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}>
      {({
        setFieldValue,
        values,
        status,
        errors,
        handleBlur,
      }: IFormikProps<IMusicProductDigitalForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'music-product-digital'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper
                      sizes={[50, 50]}
                      className='max-w-[500px]'>
                      <FormInput.Text
                        name='mainTitle.title'
                        label={t('input.label.title')}
                        autoFocus
                      />
                      <FormInput.Text
                        name='mainTitle.version'
                        label={t('input.label.version')}
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className='flex items-center gap-2 w-full justify-end'>
                    {status?.displayMode ? (
                      <LabeledStatus status={initialValues.releaseStatus} />
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          name='releaseStatus'
                          label={t('input.label.releaseStatus')}
                          placeholder={t('enum.STATUS.draft')}
                          options={getStatusOptions(
                            STATUS,
                            initialValues.releaseStatus,
                            'enum.STATUS'
                          )}
                        />
                      </div>
                    )}
                    {topbarContent}
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Image
                  fullResolutionUrl={values.productImageLink ?? undefined}
                  previewUrl={values.productImagePreviewLink ?? undefined}
                  isError={errors.file !== undefined}
                  editable={!status?.displayMode}
                  onChange={file => setFieldValue('file', file)}
                  onCancel={action => {
                    setFieldValue('productImageLink', '');
                    setFieldValue('productImagePreviewLink', '');
                    setFieldValue('file', undefined);
                    action.close();
                  }}
                  onDelete={
                    initialValues.productImageLink
                      ? action => {
                          setFieldValue('productImageLink', '');
                          setFieldValue('productImagePreviewLink', '');
                          setFieldValue('file', undefined);
                          action.close();
                        }
                      : undefined
                  }
                  imageSize={getImageSize(1500)}
                  imageSizeSelectOptions={[
                    { label: '1500 px', value: 1500 },
                    { label: '2000 px', value: 2000 },
                    { label: '2500 px', value: 2500 },
                    { label: '3000 px', value: 3000 },
                  ]}
                />
                <InfoBox.Items columns={3}>
                  <FormInput.Select
                    name='productType'
                    label={t('input.label.productType')}
                    options={getSelectOptions(
                      DIGITAL_PRODUCT_TYPE,
                      'enum.DIGITAL_PRODUCT_TYPE'
                    )}
                    isClearable={false}
                    onChange={() => {
                      setFieldValue('releaseType', RELEASE_TYPE.single);
                      setFieldValue('settlementInfo.perTrackSettlement', false);
                      if (pathname.includes('tracks')) {
                        navigate(pathname.replace('tracks', 'video'));
                      }
                      if (pathname.includes('video')) {
                        navigate(pathname.replace('video', 'tracks'));
                      }
                    }}
                  />
                  <FormInput.Select
                    name='releaseType'
                    label={t('input.label.releaseType')}
                    options={getSelectOptions(
                      RELEASE_TYPE,
                      'enum.RELEASE_TYPE'
                    )}
                    isDisabled={
                      values.productType === DIGITAL_PRODUCT_TYPE.video
                    }
                  />

                  <FormInput.Text
                    type='date'
                    name='releaseDate'
                    label={t('input.label.releaseDate')}
                  />

                  <FormInput.Select
                    name='mainTitle.language'
                    label={t('input.label.language')}
                    options={getLanguageSelectOptions(languages.data, [
                      ...values.otherTitles,
                      values.mainTitle,
                    ])}
                    value={{
                      value: values.mainTitle.language,
                      label: t(`enum.LANGUAGE.${values.mainTitle.language}`),
                    }}
                  />
                  <FormInput.Select
                    name='genre.id'
                    label={t('input.label.genre')}
                    options={
                      genres.data?.map(genre => ({
                        value: genre.id,
                        label: genre.name,
                      })) ?? []
                    }
                    isLoading={genres.isLoading}
                    value={{
                      value: values.genre.id,
                      label: values.genre.name,
                    }}
                    onChange={option => {
                      if (option) {
                        setFieldValue('genre', {
                          id: option.value,
                          name: option.label,
                        });
                      }
                    }}
                  />
                  <Input.ComplexWrapper
                    sizes={
                      !status?.displayMode &&
                      !(initialValues.ean || values.ean) &&
                      hasPermission('CodesDistributorPermissionsLIST**', 8)
                        ? [69, 30]
                        : []
                    }
                    className='w-full'>
                    <FormInput.Text
                      name='ean'
                      label={t('input.label.ean')}
                      type='number'
                    />

                    {!status?.displayMode &&
                      !(initialValues.ean || values.ean) &&
                      hasPermission('CodesDistributorPermissionsLIST**', 8) && (
                        <Button
                          onClick={async () =>
                            await generateEan.mutateAsync(undefined, {
                              onSuccess: ean => setFieldValue('ean', ean.value),
                              onError: error =>
                                onErrorShowAllErrorsToast(error),
                            })
                          }>
                          <Button.Text text={t('button.generate')} />
                        </Button>
                      )}
                  </Input.ComplexWrapper>
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='pLine.year'
                      label={`${t('input.label.year')}`}
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='pLine.name'
                      label={`${t('input.label.pline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />
                  </Input.ComplexWrapper>
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='cLine.year'
                      label={`${t('input.label.year')}`}
                      placeholder=''
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='cLine.name'
                      label={`${t('input.label.cline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />
                  </Input.ComplexWrapper>
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                disabled={disableTabs}
                tabs={[
                  ...(values.productType === DIGITAL_PRODUCT_TYPE.audio
                    ? [
                        {
                          value: musicProductDigitalTabs.tracks,
                          error: errors.tracks,
                          name: 'tracks',
                        },
                      ]
                    : []),
                  ...(values.productType === DIGITAL_PRODUCT_TYPE.video
                    ? [
                        {
                          value: musicProductDigitalTabs.video,
                          error: errors.video,
                          name: 'video',
                        },
                      ]
                    : []),
                  {
                    value: musicProductDigitalTabs.artists,
                    error: errors.artists,
                    name: 'artists',
                  },
                  {
                    value: musicProductDigitalTabs.contracts,
                    name: 'contracts',
                    error: errors.settlementInfo?.contracts,
                    permissionName: 'ContractPermissionsLIST**',
                  },
                  ...(values.productType !== DIGITAL_PRODUCT_TYPE.video ||
                  environment().node.nodeEnv === 'development'
                    ? [
                        {
                          value: musicProductDigitalTabs.delivery,
                          name: 'delivery',
                          warning:
                            status?.displayMode &&
                            !!values.hasDeliveryDiscrepancy,
                          error:
                            status?.displayMode && !!values.deliveryHasErrors
                              ? t('toast-message.delivery-error')
                              : undefined,
                          permissionName: 'DigitalDeliveryPermissionsLIST**',
                        },
                      ]
                    : []),
                  {
                    value: musicProductDigitalTabs.smartlink,
                    name: 'smartlink',
                  },
                  {
                    value: musicProductDigitalTabs.assetIds,
                    name: 'assetIds',
                  },
                  {
                    value: musicProductDigitalTabs.titles,
                    error: errors.otherTitles,
                    name: 'titles',
                  },
                  {
                    value: musicProductDigitalTabs.external,
                    error: errors.externalIds,
                    name: 'external',
                  },
                  {
                    value: musicProductDigitalTabs.statistics,
                    name: 'statistics',
                    permissionName: 'StatisticsPermissionsLIST**',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

MusicProductDigitalForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'contracts', element: <MusicProductDigitalContract /> },
    {
      path: 'contracts/history',
      element: <MusicProductDigitalContractHistory />,
    },
    {
      path: 'contracts/future',
      element: <MusicProductDigitalContractFuture />,
    },
    { path: 'external', element: <MusicProductDigitalExternal /> },
    { path: 'artists', element: <MusicProductDigitalArtists /> },
    { path: 'tracks', element: <MusicProductDigitalTracks /> },
    { path: 'video', element: <MusicProductDigitalVideo /> },
    { path: 'titles', element: <MusicProductDigitalTitles /> },
    { path: 'statistics', element: <MusicProductDigitalStatistics /> },
    { path: 'smartlink', element: <MusicProductDigitalSmartLink /> },
    { path: 'assetIds', element: <MusicProductDigitalAssetIds /> },
    {
      path: 'delivery',
      element: <MusicProductDigitalDelivery />,
    },
    {
      path: 'delivery/all',
      element: <MusicProductDigitalDeliveryAll />,
    },
    {
      path: 'delivery/:salesChannelId/:providerId',
      element: <MusicProductDigitalDeliveryDetail />,
    },
  ].filter(children => !disabledChildren?.includes(children.path));

export default MusicProductDigitalForm;
